/*
  <rebuy-cart-upsell> — mounts the Rebuy cross-sell widget inside the cart drawer.

  The drawer rebuilds itself from the Section Rendering API on every cart
  change, and both paths replace innerHTML rather than patching it:

    assets/cart-drawer.js:89   CartDrawer.renderContents() replaces #CartDrawer
    assets/cart-drawer.js:131  CartDrawerItems replaces .drawer__inner

  Anything mounted in the drawer is therefore thrown away the moment someone
  adds the upsell or nudges a quantity — which is precisely when it matters.

  The obvious fix, subscribing to PUB_SUB_EVENTS.cartUpdate, does not work.
  The two paths publish on opposite sides of the DOM swap:

    assets/cart.js:256          publishes AFTER the sections are re-rendered
    assets/product-form.js:80   publishes BEFORE renderContents() at line 104

  So a cartUpdate subscriber would run against the old DOM on the add-from-PDP
  path and be wiped a moment later.

  What is used instead is the swap itself. Assigning innerHTML re-parses this
  element into the live document, which upgrades it and fires connectedCallback
  again. The component re-hydrates because it is genuinely new each time, so it
  needs no knowledge of who re-rendered the drawer or in what order. Rebuy is
  told to re-scan on each mount, since its own boot-time pass ran long before
  this instance existed.

  This is the same mechanism <cart-note> (assets/cart.js:352) and
  <quantity-popover> already rely on to survive a drawer re-render, so it is
  load-bearing in this theme rather than a new bet.

  One dependency worth keeping: the tag that loads this file is `defer`. That
  matters on first paint, where the element is parsed by the HTML parser rather
  than assigned. A non-deferred define() would upgrade the element as its start
  tag is read, before its children exist, and the querySelector below would come
  back empty. Deferring puts the define after parsing, so children are always
  there — on first paint and on every swap alike.
*/

/*
  Rebuy adds to the cart through its own AJAX and tells the theme nothing, so
  the drawer went on showing the line items and total it was rendered with.
  The product left the widget and the cart really had it, but the drawer read
  $89.00 where it should have read $113.99, and only a reload fixed it — which
  a shopper reads as "the add failed". See #107.

  Removes never had this problem: they go through the theme's own line-item UI,
  which re-renders the drawer, which re-parses this element and re-runs
  Rebuy.init(). Only the add path escapes.

  `rebuy:cart.add` is dispatched on `document` once the add has landed, with
  the resulting cart in `detail.cart`, straight from Rebuy's bundle:

    add: function (e, t) { o.dispatchEvent("rebuy:cart.add", { cart: e, item: t }) }

  Publishing cartUpdate is all that is needed from there. CartItems subscribes
  to it (assets/cart.js:38) and onCartUpdate() re-renders every section the
  element declares, re-fetches the cart and applies the is-empty toggles. Its
  only guard is `if (event.source === 'cart-items') return`, so a different
  source runs it. That keeps this file out of the section-rendering business
  entirely — it borrows the path the rest of the theme already uses.

  `cart.add` rather than `cart.change`: change also fires for cart mutations the
  theme made itself, which already re-render, so listening to it would render
  twice for one action. Add is the only signal the theme cannot otherwise see.

  cartData is deliberately null. onCartUpdate() re-fetches the cart itself
  rather than trusting a payload, and Rebuy's detail.cart is its own shape, not
  Shopify's /cart.json.

  Registered at module scope, once. This element is destroyed and rebuilt on
  every drawer re-render, so a listener added in connectedCallback would be
  re-added on each swap.
*/
if (!window.__rebuyCartUpsellBridge) {
  window.__rebuyCartUpsellBridge = true;

  document.addEventListener('rebuy:cart.add', () => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rebuy-cart-upsell', cartData: null });
  });
}

/*
  WHICH VARIANT THE CROSS-SELL OFFERS, AND WHICH IT STARTS ON (#124)

  Rebuy hands the widget every variant of the recommended product and selects
  the first available one. On the heroes that is `1-Pack / Just Me` at $24.99:
  the legacy retail pouch the PDP ladder never sells (supply_exclude "1-Pack"
  on every hero template). It was the default on every hero cross-sell, with
  `1-Month Supply / Just Me` listed right under it. Dan named that one as
  legacy, so both are hidden by default. The list is the theme setting
  rebuy_cart_upsell_hidden_variants, so it can change without a deploy.

  HIDDEN means gone, not greyed: removed from product.variants (so it is not in
  the <select>, cannot be picked, and cannot be the default Rebuy falls back
  to) and from the option values once no remaining variant uses that value.
  A product left with no variants at all is dropped from the widget.

  THE DEFAULT for a product carrying the hero Supply option:

    1. Target = the Supply and Household of the hero line in the cart. With
       several different hero lines, the most recently added, which is the
       first one in /cart.js (Shopify lists new lines first). Kits and
       Turmeric have no Supply option, so they never count.
    2. The exact Supply x Household variant, if it is offered and available.
       That holds even when the cart already has that exact variant: it is
       still pre-selected, and adding it raises the quantity.
    3. Otherwise the nearest available one: same Household first, then the
       closest Supply in the product's own Supply order, and on a tie the
       shorter Supply (the smaller one-time charge). A hidden target lands
       the same way: a cart with 1-Month / Just Me gets 3-Month / Just Me.
    4. No hero line in the cart: the first variant that is not hidden,
       preferring available ones.

  Non-hero products (Turmeric) keep Rebuy's own default unless it is hidden.

  HOW. Rebuy's SDK events on `document`, confirmed in the loaded bundle
  (cdn.rebuyengine.com/onsite/js/global-*.js), each carrying detail.widget:

    rebuy.beforeProductsChange  {products, widget}  the raw API products,
      BEFORE initProducts(). initializeProduct() keeps a selected_variant
      that is already set and otherwise takes the first available variant,
      so filtering and selecting here is what Rebuy renders from the start.
    rebuy.ready  {widget}  the widget has rendered. /cart.js is re-read and
      the default re-applied to widget.data.products. Those are reactive:
      assigning selected_variant re-renders the select and the price.
    rebuy.beforeAdd  {product, widget}  a last guard. A hidden variant cannot
      be picked once it is out of product.variants, but if one ever is
      selected it is swapped for the default before Rebuy posts the add.

  Only the widget whose id sits in <rebuy-cart-upsell> is touched. Any other
  Rebuy widget on the page is left alone.

  CART CHANGES WHILE THE DRAWER IS OPEN. A theme-side change re-renders the
  drawer, which rebuilds this element and the widget (see the top of this
  file), so the new widget runs the whole pass again. rebuy:cart.change and
  the theme's cartUpdate also re-read /cart.js and re-apply to whatever
  widget is live. A shopper's own pick is never overwritten: the default is
  re-applied only while the selection is still the one this code set.

  Server side, the widget's product_options carry `match_variant` and
  `hide_out_of_stock_variants` (both off on 311983). match_variant matches the
  SAME variant id as the cart, which does not help when the cross-sell offers
  a different hero. There is no per-variant exclude on this widget type that
  we found, so the filter lives here, in git, next to the rest of the
  cross-sell.
*/
if (!window.__rebuyCartUpsellVariants) {
  window.__rebuyCartUpsellVariants = true;

  const norm = (value) =>
    String(value == null ? '' : value)
      .trim()
      .toLowerCase();

  const upsellConfig = () => {
    const element = document.querySelector('rebuy-cart-upsell');
    const mount = element && element.querySelector('[data-rebuy-id]');
    if (!mount) return null;
    return {
      widgetId: String(mount.getAttribute('data-rebuy-id')).trim(),
      hidden: (element.dataset.hiddenVariants || '').split(',').map(norm).filter(Boolean),
      supply: norm(element.dataset.supplyOption || 'Supply'),
      people: norm(element.dataset.peopleOption || 'Household'),
    };
  };

  const isOurWidget = (widget, config) => Boolean(widget && config && String(widget.id) === config.widgetId);

  const isHidden = (variant, config) => {
    if (!variant || config.hidden.length === 0) return false;
    const names = [variant.title, variant.option1, variant.option2, variant.option3].filter((v) => v != null).map(norm);
    return names.some((name) => config.hidden.includes(name));
  };

  // Rebuy's own variantAvailable(): only a tracked, deny-policy variant at
  // zero or below is unavailable.
  const isAvailable = (variant) =>
    !(
      variant.inventory_management &&
      norm(variant.inventory_policy) === 'deny' &&
      Number(variant.inventory_quantity) <= 0
    );

  const findOption = (product, name) => (product.options || []).find((option) => norm(option && option.name) === name);

  const optionKey = (product, name) => {
    const options = product.options || [];
    const index = options.findIndex((option) => norm(option && option.name) === name);
    if (index < 0) return null;
    return `option${Number(options[index].position) || index + 1}`;
  };

  // The hero line to match: the first cart line carrying the Supply option.
  const heroTarget = (cart, config) => {
    const items = (cart && cart.items) || [];
    for (const item of items) {
      const options = item.options_with_values || [];
      const supply = options.find((option) => norm(option.name) === config.supply);
      if (!supply) continue;
      const people = options.find((option) => norm(option.name) === config.people);
      return { supply: norm(supply.value), people: people ? norm(people.value) : null };
    }
    return null;
  };

  const pickDefault = (product, target, config) => {
    const offered = product.variants;
    const available = offered.filter(isAvailable);
    const pool = available.length > 0 ? available : offered;

    const supplyKey = optionKey(product, config.supply);
    if (!target || !supplyKey) return pool[0];

    const peopleKey = optionKey(product, config.people);
    const supplyOption = findOption(product, config.supply);
    const supplyOrder = ((supplyOption && supplyOption.values) || []).map(norm);
    const targetRank = supplyOrder.indexOf(target.supply);

    let best = pool[0];
    let bestScore = Infinity;
    pool.forEach((variant, index) => {
      const supply = norm(variant[supplyKey]);
      const peopleMiss = target.people && peopleKey && norm(variant[peopleKey]) !== target.people ? 1 : 0;

      let supplyScore = supply === target.supply ? 0 : 1;
      const rank = supplyOrder.indexOf(supply);
      if (targetRank >= 0 && rank >= 0) {
        supplyScore = 2 * Math.abs(rank - targetRank) + (rank > targetRank ? 1 : 0);
      }

      // Household outranks Supply; the index keeps remaining ties in Rebuy's order.
      const score = peopleMiss * 1e6 + supplyScore * 1e3 + index;
      if (score < bestScore) {
        bestScore = score;
        best = variant;
      }
    });
    return best;
  };

  const select = (product, variant) => {
    product.selected_variant = variant;
    product.selected_variant_id = variant.id;
    product.option1 = variant.option1;
    product.option2 = variant.option2;
    product.option3 = variant.option3;
    product.__repriseDefaultId = variant.id;
  };

  // Drops hidden variants, and option values nothing offered still uses.
  const pruneVariants = (product, config) => {
    const offered = product.variants.filter((variant) => !isHidden(variant, config));
    if (offered.length === product.variants.length) return offered.length;
    product.variants = offered;
    (product.options || []).forEach((option, index) => {
      if (!option || !Array.isArray(option.values)) return;
      const key = `option${Number(option.position) || index + 1}`;
      const used = new Set(offered.map((variant) => variant[key]));
      option.values = option.values.filter((value) => used.has(value));
    });
    return offered.length;
  };

  /*
    live=false: raw products from rebuy.beforeProductsChange.
    live=true:  the rendered widget.data.products. A selection the shopper
    made (anything but what this code last set) is left alone.
  */
  const applyToProducts = (products, config, cart, live) => {
    if (!Array.isArray(products)) return;
    const target = heroTarget(cart, config);

    for (let i = products.length - 1; i >= 0; i -= 1) {
      const product = products[i];
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) continue;

      if (pruneVariants(product, config) === 0) {
        products.splice(i, 1);
        continue;
      }

      const current = product.selected_variant;
      const currentOffered = Boolean(current) && product.variants.some((variant) => variant.id === current.id);

      if (!optionKey(product, config.supply)) {
        if (current && !currentOffered) select(product, product.variants.find(isAvailable) || product.variants[0]);
        continue;
      }

      const shopperPicked =
        live &&
        currentOffered &&
        product.__repriseDefaultId != null &&
        product.selected_variant_id !== product.__repriseDefaultId;
      if (shopperPicked) continue;

      const pick = pickDefault(product, target, config);
      if (pick) select(product, pick);
    }
  };

  const readCart = () =>
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  // Re-reads the cart and re-applies the rules to the live widget(s).
  const refreshLive = (widget) => {
    const config = upsellConfig();
    if (!config) return;
    const widgets = widget
      ? [widget]
      : ((window.Rebuy && window.Rebuy.widgets) || []).filter((w) => isOurWidget(w, config));
    const ready = widgets.filter((w) => w.data && Array.isArray(w.data.products) && w.data.products.length > 0);
    if (ready.length === 0) return;
    readCart().then((cart) => {
      if (!cart) return;
      ready.forEach((w) => applyToProducts(w.data.products, config, cart, true));
      if (typeof window.__rebuyCartUpsellSync === 'function') window.__rebuyCartUpsellSync();
    });
  };
  window.__rebuyCartUpsellRefresh = refreshLive;

  document.addEventListener('rebuy.beforeProductsChange', (event) => {
    const detail = event.detail || {};
    const config = upsellConfig();
    if (!isOurWidget(detail.widget, config)) return;
    applyToProducts(detail.products, config, detail.widget.data && detail.widget.data.cart, false);
  });

  document.addEventListener('rebuy.ready', (event) => {
    const detail = event.detail || {};
    if (isOurWidget(detail.widget, upsellConfig())) refreshLive(detail.widget);
  });

  document.addEventListener('rebuy.beforeAdd', (event) => {
    const detail = event.detail || {};
    const config = upsellConfig();
    const product = detail.product;
    if (!isOurWidget(detail.widget, config) || !product || !isHidden(product.selected_variant, config)) return;
    if (pruneVariants(product, config) === 0) return;
    const pick = pickDefault(product, heroTarget(detail.widget.data && detail.widget.data.cart, config), config);
    if (pick) select(product, pick);
  });

  document.addEventListener('rebuy:cart.change', () => refreshLive());

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => refreshLive());
  }
}

/*
  SHOW THE SLOT ONLY WHEN IT HAS PRODUCTS (#128)

  The heading and its divider are ours; Rebuy knows nothing about them. Rebuy
  answers a cart it has no rule for by rendering its shell anyway (title, an
  empty carousel, "Powered by Rebuy") and marking it is-hidden, so the old CSS
  test "has the mount any children" passed and the heading sat over nothing:
  on an empty cart, and on every single-herb cart, because data source 320414
  only has hero-triggered rules.

  The slot is now rendered `hidden` and this is the only thing that reveals
  it. The signal is the widget's own product list, widget.data.products, which
  is also what #126 filters. It is read, never written, here:

    rebuy.productsChange  {products, widget}  after Rebuy set the products,
      so after #126's beforeProductsChange filter has run on them.
    rebuy.ready / rebuy.show / rebuy.hide     the widget (re)rendered.
    refreshLive() above                       #126 re-applied to the live
      widget, which can splice a product out.
    a MutationObserver on this element        anything Rebuy re-renders
      without an event. Coalesced to one check per frame.

  The widget is matched by widget.element, the exact mount node this element
  was rendered with (Rebuy's Vue mount replaces that node in the DOM, but
  widget.element and this.mount both keep the original), so a stale widget object left over from a previous drawer render
  can never reveal the new slot. No widget, no products, Rebuy never loaded:
  the slot stays hidden. The CSS :has() rules in rebuy-cart-upsell.css are a
  fallback only.
*/
if (!window.__rebuyCartUpsellVisibility) {
  window.__rebuyCartUpsellVisibility = true;

  window.__rebuyCartUpsellSync = () => {
    document.querySelectorAll('rebuy-cart-upsell').forEach((element) => {
      if (typeof element.syncVisibility === 'function') element.syncVisibility();
    });
  };

  ['rebuy.productsChange', 'rebuy.ready', 'rebuy.show', 'rebuy.hide'].forEach((name) => {
    document.addEventListener(name, () => window.__rebuyCartUpsellSync());
  });
}

if (!customElements.get('rebuy-cart-upsell')) {
  customElements.define(
    'rebuy-cart-upsell',
    class RebuyCartUpsell extends HTMLElement {
      connectedCallback() {
        this.mount = this.querySelector('[data-rebuy-id]');
        if (!this.mount) return;

        // Rebuy loads from its app embed, asynchronously and out of our
        // control. On a cold drawer open it is usually already there; on a
        // fast first paint it is not. Retry briefly, then give up quietly —
        // a missing cross-sell is not worth a broken cart.
        this.attempts = 0;
        this.syncVisibility();
        // Observe this element, not the mount: Rebuy's Vue mount REPLACES the
        // [data-rebuy-id] node with its rendered root, so this.mount ends up
        // detached. It stays the right key for widget.element, which holds
        // the same original node.
        this.observer = new MutationObserver(() => this.scheduleSync());
        this.observer.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        this.hydrate();
      }

      disconnectedCallback() {
        if (this.retryTimer) clearTimeout(this.retryTimer);
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        if (this.observer) this.observer.disconnect();
      }

      // The number of products the widget in THIS mount is showing. 0 when
      // Rebuy has not loaded, has no widget here yet, or returned nothing.
      productCount() {
        const widgets = (window.Rebuy && window.Rebuy.widgets) || [];
        const widget = widgets.find((w) => w && w.element === this.mount);
        const products = widget && widget.data && widget.data.products;
        return Array.isArray(products) ? products.length : 0;
      }

      syncVisibility() {
        if (!this.mount) return;
        this.toggleAttribute('hidden', this.productCount() === 0);
      }

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.syncVisibility();
        });
      }

      hydrate() {
        if (!this.isConnected) return;

        if (window.Rebuy && typeof window.Rebuy.init === 'function') {
          // Re-scan for [data-rebuy-id] nodes. Rebuy's own init runs once at
          // page load, so every drawer re-render needs this nudge.
          window.Rebuy.init();
          // If Rebuy booted before this deferred file, the widget's
          // beforeProductsChange/ready already fired unheard. Apply the
          // variant rules to it directly; a widget still loading is picked
          // up by its own rebuy.ready.
          if (typeof window.__rebuyCartUpsellRefresh === 'function') window.__rebuyCartUpsellRefresh();
          this.syncVisibility();
          return;
        }

        this.attempts += 1;
        if (this.attempts > 20) {
          // ~5s. Either the app embed is off or Rebuy failed to load; the
          // slot stays hidden (#128) and the cart is unaffected.
          return;
        }

        this.retryTimer = setTimeout(() => this.hydrate(), 250);
      }
    },
  );
}
